"use client";

import { useState, type ReactNode } from "react";
import { calculateYoginiDasha, type YoginiDashaResponse } from "@/lib/api/yogini-dasha";
import type { KundaliRequest } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { YoginiDashaResult } from "./_results";
import { YoginiDashaSeoContent } from "./_sections";

export default function YoginiDashaClient({ crossLinks }: { crossLinks: ReactNode }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [result, setResult] = useState<YoginiDashaResponse | null>(null);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(undefined);

    try {
      const response = await calculateYoginiDasha({ ...data, language: "en", cycles: 3 });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return <YoginiDashaResult result={result} crossLinks={crossLinks} />;
  }

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      {/* Hero */}
      <div className="mx-auto max-w-lg px-4 sm:px-6 pt-12 sm:pt-20 pb-6">
        <div className="text-center">
          <p className="text-eyebrow font-semibold uppercase tracking-eyebrow mb-4 text-intent-brand">
            Vedic Nakshatra Dasha
          </p>
          <h1 className="text-headline sm:text-display leading-tight font-bold font-serif text-ish-dark">
            Free Yogini Dasha <span className="text-intent-brand">Calculator</span>
          </h1>
          <p className="mt-4 text-body leading-relaxed max-w-md mx-auto text-ish-text-muted">
            Calculate your 36-year Yogini Dasha cycle. Discover your active Yogini period, planetary
            rulers, and a 108-year timeline based on BPHS Chapter 47.
          </p>

          {/* Trust signals */}
          <div className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-eyebrow font-medium text-ish-text-faint">
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              BPHS Chapter 47
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              Swiss Ephemeris Precision
            </span>
            <span className="flex items-center gap-1.5">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              36-Year 8-Yogini Cycle
            </span>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="mx-auto max-w-md px-4 sm:px-6 pb-8">
        <BirthDetailsForm
          rememberBirthDetails
          onSubmit={handleSubmit}
          loading={loading}
          error={error}
          submitLabel="Calculate Yogini Dasha"
          analyticsToolName="dasha-calculator"
          analyticsStep="yogini-dasha"
        />
      </div>

      <YoginiDashaSeoContent />
    </div>
  );
}
